'use client';

import React, { useEffect, useState, useRef, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { CaretDownIcon, CheckIcon, UserIcon } from '@phosphor-icons/react';
import { Spinner } from '@heroui/spinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownRehypeRawSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import { encodeEvidenceLockerStyleProxyUriSegment, getUserEmail } from '../shared/functions';
import { fetchThreatReport, listSharedThreatReports, listThreatReports, postThreatReport } from '@/shared/cors-edge-lambda-api';
import { mergeCaseThreatReports } from '@/shared/threat-report-sharing';
import { getGatewaySession } from '@/lib/gateway-fetch';
import { reportVideoFormatFailure } from '@/lib/file-preview-telemetry';
import { useAwaitedCaseFolderNameResolver } from '@/hooks/useCaseFolderName';
import { AuthContext } from '@/app/contexts';
import { ThreatReportKey } from '@/shared/types';
import { parseThreatReportBody } from '@/shared/threat-report-body';
import { hydrateReportLanguages } from '@/shared/threat-report-language';
import { loadedThreatReportOf, type LoadedThreatReport } from '@/shared/threat-report-language-switch';
import { useThreatReportLanguageSwitch } from '@/hooks/useThreatReportLanguageSwitch';
import { useOptionalI18n } from '@/i18n/LocaleProvider';
import { localeNativeName } from '@/i18n/locales';
import { Button } from '@tranquility-ai/theme-components/button';
import { Scrollbar } from '@tranquility-ai/theme-components/primitives';
import '@tranquility-ai/theme-components/scrollbar.css';
import { useQueryClient } from '@tanstack/react-query';
import { edgeLambdaKeys } from '@/shared/cors-edge-lambda-api';
import { ThreatReportVersionHeader } from '@/components/threat-report-version-header';
import { useCaseThreatReports } from '@/hooks/useCaseThreatReports';
import { useTimePilotT } from '@/i18n/useTimePilotT';

/**
 * Threat Report Component Styles
 *
 * Styles are kept in this component file rather than globals.css to keep the component
 * self-contained and maintainable. All threat-report specific styling is defined here
 * to make it easier to modify and understand the component's visual design in one place.
 */
const threatReportStyles = `
  .threat-report,
  .threat-report p {
    font-family: var(--font-family-default);
    font-weight: 400;
    font-size: 18px;
  }

  .threat-report h1,
  .threat-report h2,
  .threat-report h3,
  .threat-report h4,
  .threat-report h5,
  .threat-report h6 {
    font-weight: 400;
    text-transform: uppercase;
    text-align: center;
  }

  .threat-report .clarion-logo {
    background-image: url('/clarion-project-logo@2x.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 228px;
    height: 96px;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }
  .threat-report .clarion-logo div { display: none; }

  .threat-report .clarion-intelligence-network {
    background-image: url('/clarion-intelligence-network@2x.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 178px;
    height: 96px;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }
  .threat-report .clarion-intelligence-network div { display: none; }

  .threat-report .clarion-seal {
    background-image: url('/clarion-seal@2x.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 410px;
    height: 365px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }

  .threat-report .clarion-seal div { display: none; }

  .threat-report-table,
  .threat-report-table div {
    /* border: 1px solid grey; */
  }

  .threat-report-heading {
    /* border: 1px solid grey; */
  }

  .threat-report .threat-report-assessment,
  .threat-report .threat-report-assessment p {
    color: var(--text-primary,#0f1d32);
    font-size: 18px;
    line-height: 1.6;
    text-align: left;
  }

  .threat-report .threat-report-assessment p {
    margin-top: 0;
    margin-bottom: 1rem;
  }

  .threat-report .threat-report-assessment h1,
  .threat-report .threat-report-assessment h2,
  .threat-report .threat-report-assessment h3,
  .threat-report .threat-report-assessment h4,
  .threat-report .threat-report-assessment h5,
  .threat-report .threat-report-assessment h6 {
    text-align: left;
    font-weight: 700;
    text-transform: none;
    margin-top: 1.75rem;
    margin-bottom: 0.75rem;
  }

  .threat-report .threat-report-assessment :is(h1, h2, h3, h4, h5, h6):first-child {
    margin-top: 0;
  }

  .threat-report .threat-report-assessment ul,
  .threat-report .threat-report-assessment ol {
    margin-left: 20px;
  }

  .threat-report .threat-report-assessment li {
    margin-left: 20px;
    margin-bottom: 0;
  }

  .threat-report .threat-report-assessment em {
    font-style: normal;
    font-weight: 700;
  }

  .threat-report .threat-report-assessment sup {
    font-size: 12px;
    vertical-align: super;
  }

  .threat-report .threat-report-assessment sup a {
    text-decoration: none;
    color: inherit;
  }

  .actionable-threats {
    padding-left: 0px;
  }

  .actionable-threats ol {
    list-style-position: inside;
    margin-left: 0;
    margin-bottom: 0;
  }

  .actionable-threats li {
    list-style-type: none;
    margin-left: 0;
    margin-bottom: 0;
  }

  .actionable-threats time {
    font-weight: 700 !important;
  }
`;

export interface ContactInfo {
  crypto?: string;
  alias?: string;
  platform?: string;
  idAccount?: string;
  url?: string;
  dateCreated?: string;
  publicPrivate?: string;
  active?: string;
}

export interface KeyAssociate {
  alias?: string;
  platform?: string;
  idAccount?: string;
  url?: string;
  dateCreated?: string;
  publicPrivate?: string;
  active?: string;
}

export interface ActionableThreat {
  date?: string;
  evidenceSummary?: string;
  link?: string;
}

export interface SocialMediaPost {
  date?: string;
  activityId: string;
  notes?: string;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
  }>;
}

/**
 * Single processing message from AI agent
 * Messages are added incrementally as they're generated and displayed in real-time
 */
export interface ProcessingMessage {
  id: string;
  type: 'agent_response' | 'tool_execution' | 'progress_update';
  content: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface ThreatAssessmentInfo {
  classification?: string;
  groupAffiliation?: string;
  ideology?: string;
}

export interface ProfileInfo {
  realName?: string;
  alias?: string;
  dob?: string;
  classification?: string;
  group?: string;
  ideology?: string;
  address?: string;
  cityState?: string;
  email?: string;
  phone?: string;
  profilePhoto?: string;
}

export interface ThreatReportProps {
  threatReportKey?: ThreatReportKey | undefined;
  userId?: string;
  tenant?: string | null;
  caseId?: string;
  reportNumber?: string; // EVIPAK#XYZ
  profile?: ProfileInfo;
  contactInfo?: ContactInfo[];
  keyAssociates?: KeyAssociate[];
  actionableThreats?: ActionableThreat[];
  socialMediaPosts?: SocialMediaPost[];
  threatAssessment?: ThreatAssessmentInfo;
  narrativeThreatAssessment?: any;
  setIsThreatReportProcessing?: (isProcessing: boolean) => void;
  /** True when a completed report body is rendered and safe to export as DOCX. */
  onThreatReportReadyChange?: (ready: boolean) => void;
  clearThreatReportKey: () => void;
  /** Open a specific generated version. The parent stores it as ``threatReportKey``. */
  onSelectVersion?: (key: ThreatReportKey) => void;
  /** Version the analyst picked from the version list; a new object for each pick. */
  pickedVersion?: ThreatReportKey;
  /** Open the create flow for a report in the language the analyst just selected. */
  onGenerateThreatReport?: () => void;
  /**
   * S3 upload time keyed by Fivecast export URL (``getSources`` ``sourceModifiedAt``).
   * The header uses it to date an export whose filename has no creation stamp.
   */
  sourceModifiedAt?: Record<string, string>;
}

/**
 * Infer media type from URL extension
 */
const inferMediaType = (url: string): 'image' | 'video' => {
  const urlLower = url.toLowerCase();
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv', '.m4v'];
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.ico'];

  for (const ext of videoExtensions) {
    if (urlLower.includes(ext)) {
      return 'video';
    }
  }

  for (const ext of imageExtensions) {
    if (urlLower.includes(ext)) {
      return 'image';
    }
  }

  // Default to image if unknown
  return 'image';
};

/**
 * Extract value from a key-value pair line
 * Handles formats like "- Key: Value" or "- Key: Value"
 */
const extractKeyValue = (line: string): { key: string; value: string } | null => {
  const match = line.match(/^[\s\t]*-[\s\t]*(.+?):[\s\t]*(.+)$/);
  if (match) {
    return {
      key: match[1].trim(),
      value: match[2].trim(),
    };
  }
  return null;
};

/**
 * Convert S3 URL to proxy URL for display
 */
const convertS3UrlToProxyUrl = (url: string | undefined): string => {
  const t = (url ?? '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(t)}`;
};

const POLLING_TIME_LIMIT = 1800; // 30 minutes
const POLLING_INTERVAL = 3000; // 3 seconds
const POLLING_RAMPUP = 1000; // 1 seconds

/**
 * ThreatReport Component
 *
 * Template component for displaying threat reports matching Google Doc formatting.
 * Uses Jost font with Inter fallback, flexbox-based layouts for future responsiveness.
 */
const CLARION_CIN_URL = 'https://clarionproject.org/cin';
const CLARION_SITE_URL = 'http://clarionproject.org';
const CLARION_EMAIL = 'intel@clarionproject.org';

function textWithLinks(
  message: string,
  links: { text: string; href: string; external?: boolean }[],
) {
  const pieces: Array<{ kind: 'text'; value: string } | { kind: 'link'; link: (typeof links)[number] }> = [
    { kind: 'text', value: message },
  ];
  for (const link of links) {
    const next: typeof pieces = [];
    for (const piece of pieces) {
      if (piece.kind !== 'text' || !piece.value.includes(link.text)) {
        next.push(piece);
        continue;
      }
      const [before, ...rest] = piece.value.split(link.text);
      next.push({ kind: 'text', value: before });
      rest.forEach((part) => {
        next.push({ kind: 'link', link });
        next.push({ kind: 'text', value: part });
      });
    }
    pieces.splice(0, pieces.length, ...next);
  }
  return (
    <>
      {pieces.map((piece, index) => (
        piece.kind === 'text' ? (
          <React.Fragment key={index}>{piece.value}</React.Fragment>
        ) : (
          <a
            key={index}
            href={piece.link.href}
            target={piece.link.external ? '_blank' : undefined}
            rel={piece.link.external ? 'noopener noreferrer' : undefined}
          >
            {piece.link.text}
          </a>
        )
      ))}
    </>
  );
}

const ThreatReport: React.FC<ThreatReportProps> = ({
  threatReportKey,
  userId,
  tenant,
  caseId,
  reportNumber,
  profile,
  contactInfo,
  keyAssociates,
  actionableThreats,
  socialMediaPosts,
  threatAssessment,
  narrativeThreatAssessment,
  setIsThreatReportProcessing,
  onThreatReportReadyChange,
  clearThreatReportKey,
  onSelectVersion,
  pickedVersion,
  onGenerateThreatReport,
  sourceModifiedAt,
}) => {
  const i18n = useOptionalI18n();
  const t = useTimePilotT();
  const [finalNarrativeThreatAssessmentMd, setFinalNarrativeThreatAssessmentMd] = useState<string | null>(null);
  const awaitCaseFolderName = useAwaitedCaseFolderNameResolver();
  const processingMessagesRef = useRef<HTMLDivElement>(null);
  const pollForThreatReportTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** After revert we clear the parent key; skip one keyless effect cycle to avoid a second loadThreatReportFromTable. */
  const suppressNextKeylessLoadEffectRef = useRef(false);
  /** Drops a fetch that finishes after the user has already switched versions. */
  const reportLoadGenerationRef = useRef(0);
  /** Language switch loads the next version without blanking the one on screen. */
  const keepVisibleOnNextKeyRef = useRef(false);

  // Local state to override isProcessing prop for testing the Complete button
  const [localIsProcessing, setLocalIsProcessing] = useState<boolean>(true);
  const [pollForThreatReport, setPollForThreatReport] = useState<number>(0);
  const [processingMessages, setProcessingMessages] = useState<ProcessingMessage[]>([]);
  const [startCheckingForFinalResult, setStartCheckingForFinalResult] = useState<boolean>(false);
  const [threatReportJson, setThreatReportJson] = useState<any>(null);
  const [loadedReport, setLoadedReport] = useState<LoadedThreatReport | null>(null);
  const [threatReportStatus, setThreatReportStatus] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [threatReportKeyLocal, setThreatReportKeyLocal] = useState<ThreatReportKey | undefined>(threatReportKey);

  // Final result vars that repolace props when completed
  const [finalReportNumber, setFinalReportNumber] = useState<string | undefined>(reportNumber);
  const [finalProfile, setFinalProfile] = useState<ProfileInfo | undefined>(profile);
  const [finalContactInfo, setFinalContactInfo] = useState<ContactInfo[] | undefined>(contactInfo);
  const [finalKeyAssociates, setFinalKeyAssociates] = useState<KeyAssociate[] | undefined>(keyAssociates);
  const [finalActionableThreats, setFinalActionableThreats] = useState<ActionableThreat[] | undefined>(actionableThreats);
  const [finalSocialMediaPosts, setFinalSocialMediaPosts] = useState<SocialMediaPost[] | undefined>(socialMediaPosts);
  const [finalThreatAssessment, setFinalThreatAssessment] = useState<ThreatAssessmentInfo | undefined>(threatAssessment);
  const [finalNarrativeThreatAssessment, setFinalNarrativeThreatAssessment] = useState<any | null>(narrativeThreatAssessment);

  const { idToken, accessToken } = useContext(AuthContext);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (threatReportKey?.epochTimeSecs && threatReportKey?.epochTimeSecs > 0) {
      const generation = ++reportLoadGenerationRef.current;
      setThreatReportKeyLocal(threatReportKey);
      if (pollForThreatReportTimeoutRef.current) {
        clearTimeout(pollForThreatReportTimeoutRef.current);
        pollForThreatReportTimeoutRef.current = null;
      }
      setPollForThreatReport(0);
      setErrorText(null);
      setThreatReportStatus(null);
      if (keepVisibleOnNextKeyRef.current) {
        keepVisibleOnNextKeyRef.current = false;
        void loadThreatReportFromTable(0, { generation });
        return;
      }
      setThreatReportJson(null);
      setLoadedReport(null);
      setProcessingMessages([]);
      setLocalIsProcessing(true);
      void loadThreatReportFromTable(0, { generation });
    } else {
      if (suppressNextKeylessLoadEffectRef.current) {
        suppressNextKeylessLoadEffectRef.current = false;
        setThreatReportKeyLocal(undefined);
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        return;
      }
      setThreatReportKeyLocal(undefined);
      if (pollForThreatReportTimeoutRef.current) {
        clearTimeout(pollForThreatReportTimeoutRef.current);
        pollForThreatReportTimeoutRef.current = null;
      }
      setErrorText(null);
      setPollForThreatReport(0);
      setThreatReportJson(null);
      setLoadedReport(null);
      setThreatReportStatus(null);
      setLocalIsProcessing(true);
      setProcessingMessages([]);
      // Pass 0: state from setPollForThreatReport(0) is not visible until the next render, so the
      // polling counter must not use the stale closure value inside loadLastThreatReportFromTable.
      const generation = ++reportLoadGenerationRef.current;
      loadThreatReportFromTable(0, { generation });
    }
  }, [threatReportKey?.epochTimeSecs, threatReportKey?.chatKey]);

  useEffect(() => {
    if (threatReportJson) {
      setFinalReportNumber(threatReportJson.reportNumber);
      setFinalProfile(threatReportJson.profile);
      setFinalContactInfo(threatReportJson.contactInfo);
      setFinalKeyAssociates(threatReportJson.keyAssociates);
      setFinalActionableThreats(threatReportJson.actionableThreats);
      setFinalSocialMediaPosts(threatReportJson.socialMediaPosts);
      setFinalThreatAssessment(threatReportJson.threatAssessment);
      setFinalNarrativeThreatAssessment(threatReportJson.narrativeThreatAssessment);
    } else {
      setFinalReportNumber(undefined);
      setFinalProfile(undefined);
      setFinalContactInfo(undefined);
      setFinalKeyAssociates(undefined);
      setFinalActionableThreats(undefined);
      setFinalSocialMediaPosts(undefined);
      setFinalThreatAssessment(undefined);
      setFinalNarrativeThreatAssessment(null);
    }
  }, [threatReportJson]);

  // Export DOCX is only valid once a completed report body is on screen (not
  // processing / not_found / error empty states).
  useEffect(() => {
    const ready = Boolean(!localIsProcessing && threatReportJson);
    onThreatReportReadyChange?.(ready);
    return () => {
      onThreatReportReadyChange?.(false);
    };
  }, [localIsProcessing, threatReportJson, onThreatReportReadyChange]);

  const fetchThreatReportFromTable = async (currentThreatReportKey?: ThreatReportKey, generation?: number): Promise<any | null> => {
    if (!caseId) {
      return null;
    }
    // if threatReportKey is provided, we are looking for that specific threat report
    const { chatKey, sessionId, epochTimeSecs, requestType } = currentThreatReportKey || {};
    const body = {
      action: 'getThreatReport',
      chatKey: chatKey,
      sessionId: sessionId,
      epochTimeSecs: epochTimeSecs,
      requestType: requestType,
    };
    try {
      const session = await getGatewaySession();
      if (!session) return null;
      let data: Record<string, unknown>;
      if (epochTimeSecs) {
        data = await postThreatReport({ cellId: session.cellId, accessToken: session.accessToken, body });
      } else {
        data = await fetchThreatReport({
          cellId: session.cellId,
          accessToken: session.accessToken,
          caseId: caseId || '',
          // Selects the row: edge-lambda rebuilds the assistant's ``chat_key``
          // from this, and the write side keyed it on the folder segment.
          caseFolderName: await awaitCaseFolderName(caseId),
        });
      }
      if (generation != null && generation !== reportLoadGenerationRef.current) return null;
      const completed = data.status === 'completed' || (!data.status && !!data.threat_report_json && (data.threat_report_json as string).trim() !== '');
      const error = data.status === 'error';
      setThreatReportStatus(data.status ? data.status as string : (completed ? 'completed' : 'unknown'));
      if ((completed || error) && data?.epoch_time_secs) {
        setThreatReportJson(parseThreatReportBody(data));
        setLoadedReport(completed ? loadedThreatReportOf(data, caseId) : null);
      }
      // Return the row for any specific GET/POST hit with epoch_time_secs so loadThreatReportFromTable
      // can drive polling and processing UI (not only terminal completed/error states).
      if (data?.epoch_time_secs) {
        // Do not schedule polling here: loadThreatReportFromTable owns pollForThreatReportTimeoutRef
        // and enforces POLLING_TIME_LIMIT. A timer here was left running when load exited early at the limit.
        return data;
      }
      return null;
    } catch {
      return null;
    }
  }

  const deleteErrorThreatReportFromTable = async (currentThreatReportKey?: ThreatReportKey) => {
    const { chatKey, sessionId, epochTimeSecs, requestType } = currentThreatReportKey || {};
    const body = {
      action: 'deleteErrorThreatReport',
      chatKey: chatKey,
      sessionId: sessionId,
      epochTimeSecs: epochTimeSecs,
      requestType: requestType,
    };
    try {
      const session = await getGatewaySession();
      if (!session) return false;
      await postThreatReport({ cellId: session.cellId, accessToken: session.accessToken, body });
      void queryClient.invalidateQueries({
        queryKey: [...edgeLambdaKeys.all, 'threatReportVersions'],
      });
      return true;
    } catch {
      return false;
    }
  }

  const revertToLastThreatReport = async (currentThreatReportKey?: ThreatReportKey) => {
    setThreatReportStatus(null);
    setThreatReportJson(null);
    setLoadedReport(null);
    setErrorText(null);
    setPollForThreatReport(0);
    const generation = ++reportLoadGenerationRef.current;
    setProcessingMessages([]);
    setLocalIsProcessing(true);
    await deleteErrorThreatReportFromTable(currentThreatReportKey);
    setThreatReportKeyLocal(undefined);
    try {
      await loadThreatReportFromTable(0, { useLatestFromTable: true, generation });
    } finally {
      suppressNextKeylessLoadEffectRef.current = true;
      clearThreatReportKey();
    }
  }

  const loadThreatReportFromTable = async (
    pollElapsedOverride?: number,
    options?: { useLatestFromTable?: boolean; generation?: number },
  ) => {
    const generation = options?.generation ?? reportLoadGenerationRef.current;
    const stillCurrent = () => generation === reportLoadGenerationRef.current;
    setThreatReportStatus(null);
    setErrorText(null);
    try {
      const pollElapsed = pollElapsedOverride !== undefined ? pollElapsedOverride : pollForThreatReport;
      const fetchKey = options?.useLatestFromTable ? undefined : threatReportKey;
      const lastThreatReport = await fetchThreatReportFromTable(fetchKey, generation);
      if (!stillCurrent()) return;
      if (!lastThreatReport) {
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        setPollForThreatReport(0);
        setProcessingMessages([]);
        setThreatReportStatus('not_found');
        setErrorText('Could not load any threat report.');
        setLocalIsProcessing(false);
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      }

      // Check if we have a completed threat report (with threat_report_json)
      const oldStyleCompleted = !!lastThreatReport?.threat_report_json && lastThreatReport?.threat_report_json.trim() !== '';
      const completed = lastThreatReport?.status === 'completed';
      const reallyCompleted = completed || (!lastThreatReport?.status && oldStyleCompleted);
      if (reallyCompleted && lastThreatReport?.threat_report_json) {
        setThreatReportStatus(!!lastThreatReport?.status ? lastThreatReport.status : (oldStyleCompleted ? 'completed' : 'unknown'));
        // Completed report exists - load it and stop processing
        setStartCheckingForFinalResult(false);
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        setPollForThreatReport(0);
        setProcessingMessages([]);
        setLocalIsProcessing(false);
        setThreatReportJson(parseThreatReportBody(lastThreatReport));
        setLoadedReport(loadedThreatReportOf(lastThreatReport, caseId));
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      } else if (lastThreatReport?.status === 'error') {
        const lastThreatReportKey = {
          chatKey: lastThreatReport.chat_key,
          sessionId: lastThreatReport.session_id,
          epochTimeSecs: lastThreatReport.epoch_time_secs,
          requestType: lastThreatReport.request_type,
        };
        setThreatReportKeyLocal(lastThreatReportKey);
        setThreatReportStatus('error');
        let resolvedError: string | null = null;
        try {
          const answer = JSON.parse(lastThreatReport.answer);
          if (typeof answer?.output === 'string' && answer.output.trim() !== '') {
            resolvedError = answer.output;
          }
        } catch {
          // use fallbacks below
        }
        if (!resolvedError && typeof lastThreatReport.output === 'string' && lastThreatReport.output.trim() !== '') {
          resolvedError = lastThreatReport.output;
        }
        setErrorText(resolvedError ?? 'There was an error generating the last threat report.');
        setLocalIsProcessing(false);
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      }

      if (lastThreatReport?.answer) {
        let answer = null;
        try {
          answer = JSON.parse(lastThreatReport.answer);
        } catch (error) {
          // try again
        }
        if (answer) {
          const status = ('status' in answer) ? answer.status : 'unknown';
          const isInitializing = status === 'initializing';
          const isInProgress = status === 'inprogress';
          const isCompleted = status === 'completed';
          const hasError = status === 'error';
          const isPrelimary = ('is_preliminary' in answer) && answer.is_preliminary === true;
          const containsMessages = !!answer && ('messages' in answer) && answer.messages.length > 0 ? true : false;
          const isPreliminaryResult = isInitializing || isInProgress || isPrelimary;
          // Do not treat empty messages as "final" while still initializing/in progress (avoids empty template flash).
          const isFinalResult = isPreliminaryResult
            ? false
            : isCompleted || (containsMessages && !isPreliminaryResult);
          const newLocalIsProcessing = !hasError && !isFinalResult;
          setLocalIsProcessing(newLocalIsProcessing);
          setThreatReportStatus(status);
          if (!hasError) {
            if (!newLocalIsProcessing || (startCheckingForFinalResult && isFinalResult)) {
              setStartCheckingForFinalResult(false);
              if (pollForThreatReportTimeoutRef.current) {
                clearTimeout(pollForThreatReportTimeoutRef.current);
                pollForThreatReportTimeoutRef.current = null;
              }
              setPollForThreatReport(0);
              setProcessingMessages([]);
              setLocalIsProcessing(false);
              setThreatReportJson(parseThreatReportBody(lastThreatReport));
              setLoadedReport(loadedThreatReportOf(lastThreatReport, caseId));
              if (setIsThreatReportProcessing) {
                setIsThreatReportProcessing(false);
              }
              return;
            } else if (!isFinalResult) {
              if (!startCheckingForFinalResult) {
                setStartCheckingForFinalResult(true);
              }
              const processingMessages = answer?.messages?.map((message: ProcessingMessage) => {
                return {
                  id: message.id,
                  content: message.content,
                  timestamp: message.timestamp,
                  type: message.type,
                  metadata: message.metadata,
                };
              });
              setLocalIsProcessing(true);
              setProcessingMessages(processingMessages);
              if (pollElapsed < POLLING_TIME_LIMIT) {
                if (pollForThreatReportTimeoutRef.current) {
                  clearTimeout(pollForThreatReportTimeoutRef.current);
                  pollForThreatReportTimeoutRef.current = null;
                }
                pollForThreatReportTimeoutRef.current = setTimeout(() => {
                  setPollForThreatReport((prev) => prev + POLLING_INTERVAL / 1000);
                }, POLLING_INTERVAL);
              } else {
                if (pollForThreatReportTimeoutRef.current) {
                  clearTimeout(pollForThreatReportTimeoutRef.current);
                  pollForThreatReportTimeoutRef.current = null;
                }
                setLocalIsProcessing(false);
                if (setIsThreatReportProcessing) {
                  setIsThreatReportProcessing(false);
                }
              }
              return;
            }
            if (pollElapsed < POLLING_TIME_LIMIT) {
              if (pollForThreatReportTimeoutRef.current) {
                clearTimeout(pollForThreatReportTimeoutRef.current);
                pollForThreatReportTimeoutRef.current = null;
              }
              pollForThreatReportTimeoutRef.current = setTimeout(() => {
                setPollForThreatReport((prev) => prev + POLLING_INTERVAL / 1000);
              }, POLLING_INTERVAL);
            }
          } else {
            setThreatReportStatus('error');
            setErrorText(answer.output);
            const lastThreatReportKey = {
              chatKey: lastThreatReport.chat_key,
              sessionId: lastThreatReport.session_id,
              epochTimeSecs: lastThreatReport.epoch_time_secs,
              requestType: lastThreatReport.request_type,
            };
            setThreatReportKeyLocal(lastThreatReportKey);
            setStartCheckingForFinalResult(false);
            if (pollForThreatReportTimeoutRef.current) {
              clearTimeout(pollForThreatReportTimeoutRef.current);
              pollForThreatReportTimeoutRef.current = null;
            }
            setPollForThreatReport(0);
            setProcessingMessages([]);
            if (setIsThreatReportProcessing) {
              setIsThreatReportProcessing(false);
            }
            return;
          }
        }
      } else {
        // No threat report data or old timestamp - stop processing after timeout
        if (pollElapsed >= POLLING_TIME_LIMIT) {
          if (pollForThreatReportTimeoutRef.current) {
            clearTimeout(pollForThreatReportTimeoutRef.current);
            pollForThreatReportTimeoutRef.current = null;
          }
          setLocalIsProcessing(false);
          if (setIsThreatReportProcessing) {
            setIsThreatReportProcessing(false);
          }
        } else if (pollElapsed < POLLING_TIME_LIMIT) {
          if (pollForThreatReportTimeoutRef.current) {
            clearTimeout(pollForThreatReportTimeoutRef.current);
            pollForThreatReportTimeoutRef.current = null;
          }
          const delay = pollElapsed < POLLING_INTERVAL ? POLLING_RAMPUP : POLLING_INTERVAL;
          const incrementSecs =
            (pollElapsed < POLLING_INTERVAL ? POLLING_RAMPUP : POLLING_INTERVAL) / 1000;
          pollForThreatReportTimeoutRef.current = setTimeout(() => {
            setPollForThreatReport((prev) => prev + incrementSecs);
          }, delay);
        }
      }
    } catch (error) {
      if (!stillCurrent()) return;
      if (pollForThreatReportTimeoutRef.current) {
        clearTimeout(pollForThreatReportTimeoutRef.current);
        pollForThreatReportTimeoutRef.current = null;
      }
      setPollForThreatReport(0);
      setProcessingMessages([]);
      setErrorText('Could not load any threat report.');
      setThreatReportStatus('not_found');
      setLocalIsProcessing(false);
      if (setIsThreatReportProcessing) {
        setIsThreatReportProcessing(false);
      }
    }
  }

  useEffect(() => {
    if (pollForThreatReport > 0) {
      loadThreatReportFromTable();
    }
  }, [pollForThreatReport]);

  useEffect(() => {
    const viewport = processingMessagesRef.current;
    if (!localIsProcessing || !viewport || !processingMessages?.length) return;
    // Pin after layout. The ref is the overflow element inside Scrollbar, whose
    // host is not the scrolling viewport.
    const pinLatest = () => {
      viewport.scrollTop = viewport.scrollHeight;
    };
    pinLatest();
    const frame = requestAnimationFrame(pinLatest);
    return () => cancelAnimationFrame(frame);
  }, [processingMessages, localIsProcessing]);

  useEffect(() => {
    if (finalNarrativeThreatAssessment) {
      // if you change this, make sure to keep the \n\n or put everything in a template string
      let md = `## ${t('threatReport.body.narrative')}\n\n` +
      `### ${t('threatReport.body.threatLevel')}\n\n` +
      `- ${finalNarrativeThreatAssessment.threatLevel}\n\n` +
      `### ${t('threatReport.body.executiveSummary')}\n\n` +
      `- ${finalNarrativeThreatAssessment.executiveSummary}\n\n` +
      `### ${t('threatReport.body.contentChronology')}\n\n` +
      `${finalNarrativeThreatAssessment.contentChronology.split('\n\n').map((point: string) => `- ${point}`).join('\n\n')}` +
      `\n\n` +
      `### ${t('threatReport.body.keyConcerningElements')}\n\n` +
      `${finalNarrativeThreatAssessment.keyConcerningElements}\n\n` +
      `### ${t('threatReport.body.recommendedActions')}\n\n` +
      `${finalNarrativeThreatAssessment.recommendedActions}\n\n`;

      if (finalNarrativeThreatAssessment.conclusion && finalNarrativeThreatAssessment.conclusion.trim() !== '') {
        md += `### ${t('threatReport.body.conclusion')}\n\n` +
        `- ${finalNarrativeThreatAssessment.conclusion}\n\n`;
      }
      setFinalNarrativeThreatAssessmentMd(md);
    } else {
      setFinalNarrativeThreatAssessmentMd(null);
    }
  }, [finalNarrativeThreatAssessment, caseId, t]);

  // [REPLACE COLOR] - Classification box color (yellow/gold)
  const classificationBoxColor = '#FED766';
  // [REPLACE COLOR] - Group affiliation box color (light blue)
  const groupBoxColor = '#A8DADC';

  // Preload critical images for faster loading
  useEffect(() => {
    const preloadImages = [
      '/clarion-project-logo@2x.png',
      '/clarion-intelligence-network@2x.png',
      '/clarion-seal@2x.png',
    ];

    preloadImages.forEach((src) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'high';
      document.head.appendChild(link);
    });

    // Cleanup function to remove preload links when component unmounts
    return () => {
      preloadImages.forEach((src) => {
        const existingLink = document.querySelector(`link[href="${src}"]`);
        if (existingLink) {
          document.head.removeChild(existingLink);
        }
      });
    };
  }, []);

  const { reports: caseReports } = useCaseThreatReports(caseId);
  const { promptOpen: languagePromptOpen, isSwitching: isLanguageSwitching, dismissPrompt: dismissLanguagePrompt } =
    useThreatReportLanguageSwitch({
      locale: i18n?.locale ?? "en",
      caseId,
      enabled: Boolean(i18n) && Boolean(onSelectVersion) && !localIsProcessing,
      blocked: threatReportStatus === "error" || threatReportStatus === "not_found",
      loadedReport,
      pickedVersion,
      lookupReports: async () => {
        const fetchCaseId = caseId?.trim() || "";
        if (!fetchCaseId) throw new Error("No case is open");
        const session = await getGatewaySession();
        if (!session) throw new Error("No gateway session");
        const listArgs = {
          cellId: session.cellId,
          accessToken: session.accessToken,
          caseId: fetchCaseId,
          caseFolderName: await awaitCaseFolderName(fetchCaseId),
        };
        const [own, shared] = await Promise.all([
          listThreatReports(listArgs),
          listSharedThreatReports(listArgs).catch(() => []),
        ]);
        let email = "";
        if (idToken) {
          try {
            email = getUserEmail(jwtDecode(idToken));
          } catch {
            email = "";
          }
        }
        const listed = mergeCaseThreatReports(own, shared, email);
        return hydrateReportLanguages(listed, (report) =>
          postThreatReport({
            cellId: session.cellId,
            accessToken: session.accessToken,
            body: {
              action: "getThreatReport",
              chatKey: report.chatKey,
              sessionId: report.sessionId,
              epochTimeSecs: report.epochTimeSecs,
              requestType: report.requestType,
            },
          }),
        );
      },
      onOpen: (report) => {
        // Already the selected key: its body is loading and no key effect will run.
        if (
          threatReportKey?.chatKey === report.chatKey &&
          Number(threatReportKey?.epochTimeSecs) === Number(report.epochTimeSecs)
        ) {
          return;
        }
        keepVisibleOnNextKeyRef.current = true;
        onSelectVersion?.({
          chatKey: report.chatKey,
          sessionId: report.sessionId,
          epochTimeSecs: report.epochTimeSecs,
          requestType: report.requestType,
        });
      },
    });
  const languageName = localeNativeName(i18n?.locale ?? "en");
  const languagePromptTitle = i18n
    ? i18n.t("threatReport.languageMissingTitle", { language: languageName })
    : `No ${languageName} threat report yet`;
  const languagePromptBody = i18n
    ? i18n.t("threatReport.languageMissingBody", { language: languageName })
    : `There isn't a ${languageName} threat report for this case yet. Generate one to read the report in ${languageName}.`;
  const languagePromptGenerate = i18n
    ? i18n.t("threatReport.languageMissingGenerate")
    : "Generate threat report";
  const languagePromptDismiss = i18n
    ? i18n.t("threatReport.languageMissingDismiss")
    : "Not now";
  const languageSwitchLabel = i18n
    ? i18n.t("threatReport.languageLoading", { language: languageName })
    : `Loading the ${languageName} threat report…`;
  const openReport = threatReportKeyLocal
    ? caseReports.find(
        (report) =>
          report.chatKey === threatReportKeyLocal.chatKey &&
          report.epochTimeSecs === threatReportKeyLocal.epochTimeSecs,
      )
    : undefined;
  const canManageReport = openReport ? openReport.isOwner : true;

  const versionHeader = (
    <ThreatReportVersionHeader
      caseId={caseId}
      currentChatKey={threatReportKeyLocal?.chatKey}
      currentEpochTimeSecs={threatReportKeyLocal?.epochTimeSecs}
      sourceModifiedAt={sourceModifiedAt}
    />
  );

  if (threatReportStatus === 'error' || threatReportStatus === 'not_found') {
    return (
      <div className="flex h-full min-h-0 flex-col bg-[var(--color-ink-085,#0f1d32)]">
      <Scrollbar className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-[30px] py-[30px] text-center text-white">
        <div className="w-full pb-[30px]">{versionHeader}</div>
        {errorText && errorText.trim() !== '' ? (
          <h1 className="text-center">{errorText}</h1>
        ) : (
          <h1 className="text-center">{t('threatReport.error.generateFailed')}</h1>
        )}
        {canManageReport && threatReportStatus === 'error' && (
          <h2 className="text-center">{t('threatReport.error.replace')}</h2>
        )}
        {canManageReport && threatReportKeyLocal && threatReportStatus === 'error' && (
          <div className="cursor-pointer text-center">
            <Button style="text" color="light" background="inverse" onClick={() => revertToLastThreatReport(threatReportKeyLocal)}>
              {t('threatReport.error.revert')}
            </Button>
          </div>
        )}
      </Scrollbar>
      </div>
    )
  }
  return (
    <>
      <style>{threatReportStyles}</style>
      <div className="relative flex h-full min-h-0 flex-col bg-[var(--color-ink-085,#0f1d32)]">
      {isLanguageSwitching ? (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-[#0f1d32]/80 text-white"
          role="status"
          aria-live="polite"
        >
          <Spinner variant="spinner" size="lg" className="[&_i]:bg-blue-300" aria-hidden />
          <span className="text-sm">{languageSwitchLabel}</span>
        </div>
      ) : null}
      {languagePromptOpen ? (
        <div
          className="mx-[30px] mt-[30px] flex flex-col gap-3 rounded-md border border-[#2a4a6c] bg-[#12294a] px-4 py-3 text-white"
          role="dialog"
          aria-labelledby="threat-report-language-title"
        >
          <h2 id="threat-report-language-title" className="text-base font-medium">
            {languagePromptTitle}
          </h2>
          <p className="text-sm text-white/80">{languagePromptBody}</p>
          <div className="flex flex-wrap gap-2">
            {onGenerateThreatReport ? (
              <Button
                style="solid"
                color="accent"
                onClick={() => {
                  dismissLanguagePrompt();
                  onGenerateThreatReport();
                }}
              >
                {languagePromptGenerate}
              </Button>
            ) : null}
            <Button style="text" color="light" background="inverse" onClick={dismissLanguagePrompt}>
              {languagePromptDismiss}
            </Button>
          </div>
        </div>
      ) : null}
      <Scrollbar className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="pt-[30px]">{versionHeader}</div>
      <div id="threat-report-docx" className="threat-report w-full min-w-0 py-[30px]">
      {/* AI Processing Section */}
      {(localIsProcessing) && (
        <div className="w-full max-w-full bg-[#0A1520] mb-4 rounded-lg">
          <div className="flex flex-row h-[calc(100vh-280px)] max-h-[calc(100vh-280px)] px-[20px] py-6">
            <Scrollbar className="flex h-full min-h-0 w-full">
              <div
                ref={processingMessagesRef}
                className="m-0 flex h-full min-h-0 w-full flex-col gap-0 overflow-y-auto text-[14px] font-light leading-snug text-white"
                aria-live="polite"
                aria-busy={localIsProcessing}
                role="status"
              >
              {(!processingMessages || processingMessages.length === 0) && (
                <div className="flex h-full w-full items-center justify-center">
                  <Spinner variant="spinner" size="lg" className="[&_i]:bg-blue-300" aria-label={t('threatReport.loading')} />
                </div>
              )}
              {processingMessages && processingMessages.length > 0 && processingMessages.map((message: ProcessingMessage, index: number) => (
                <div
                  key={message.id}
                  className="box-border w-full h-fit flex flex-row gap-0 items-center text-white font-light text-[14px] leading-snug m-0 min-h-fit"
                >
                  <div className="min-w-[31px] flex flex-row items-center justify-start p-0">
                    {index === processingMessages.length - 1 ? (
                      <div className="relative z-1 flex flex-row gap-2 items-start justify-start w-full min-h-[32px]">
                        <div className="w-[27px] h-[27px] p-[3px] box-border flex items-center justify-center bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                          <Spinner variant="spinner" size="sm" className="[&_i]:bg-blue-300" />
                        </div>
                        <div className="box-border flex font-light w-full text-[14px] leading-snug min-h-[32px] mt-[4px] text-blue-300">
                          {message.content === 'Analysis complete.' ? t('threatReport.processing.analysisReady') : message.content}
                        </div>
                      </div>
                    ) : (
                      <div className="relative z-1 flex flex-row-reverse gap-2 items-start justify-start w-full min-h-[21px]">
                        <div className="relative z-1 box-border flex font-light w-full text-[14px] leading-snug pb-[10px] min-h-[32px] text-white
                          pl-[20px] -ml-[19px] mt-[2px] border-l-[1px] border-white border-solid"
                        >
                          {message.content}
                        </div>
                        <div className="relative z-2 box-border flex items-center justify-start w-[21px] h-[21px] max-h-[21px] min-h-[21px]
                            p-[3px] ml-[3px] bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full"
                        >
                          <CheckIcon size={15} weight="bold" className="relative z-3 text-green-500" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
            </Scrollbar>
          </div>
        </div>
      )}
      {/* Template Wrapper - Only show when not processing */}
      {!localIsProcessing && (
        <div className="mx-auto min-h-full w-full max-w-full border border-gray-300 bg-white">
        <div className="flex flex-col p-8 font-jost text-[color:var(--text-primary,#0f1d32)]">

          {/* Header Section (Page 1) */}
          <header>

            <section aria-label={t('threatReport.body.logos')} className="mb-8">
              <figure className="flex justify-center m-0">
                <img src="/clarion-tr-logos@2x.png" alt={t('threatReport.body.logosAlt')} className="scale-50" />
              </figure>
            </section>

            <>
              {/* Emblem/Seal */}
              <figure className="flex justify-center my-8">
                <img height={365} width={410} src="/clarion-seal@2x.png" alt={t('threatReport.body.sealAlt')} />
              </figure>

              {/* Classification Title */}
              <h1 className="flex flex-col items-center text-center mb-8 mt-4">
                {t('threatReport.body.lawEnforcementSensitive')}
                <br />
                {t('threatReport.body.forLawEnforcement')}
                <br />
                {t('threatReport.body.distributionOnly')}
              </h1>

              {/* Introduction Paragraphs */}
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                {t('threatReport.body.purposeDiscovered')}
              </p>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                {textWithLinks(t('threatReport.body.clarionProject', { url: CLARION_CIN_URL }), [
                  { text: CLARION_CIN_URL, href: CLARION_CIN_URL, external: true },
                ])}
              </p>

              {/* Contact Information */}
              <address className="mb-12 mt-8 max-w-4xl mx-auto not-italic leading-relaxed">
                {textWithLinks(
                  t('threatReport.body.reportContact', { number: finalReportNumber, email: CLARION_EMAIL }),
                  [{ text: CLARION_EMAIL, href: `mailto:${CLARION_EMAIL}` }],
                )}
              </address>
            </>
          </header>

          {/* Main Content */}
          <main aria-label={t('threatReport.body.content')}>

          {/* Profile Section */}
          <section id="profile" aria-labelledby="profile-heading" className="mb-12">
            <h2 id="profile-heading" className="mb-6 text-2xl font-bold uppercase">{t('threatReport.body.profile')}</h2>

            {/* Profile Section with three columns: two for fields, one for photo */}
            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <tbody>
                {/* Row 1: Real Name and Alias */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.realName')}</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.realName || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.alias')}</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.alias || ''}</td>
                  <td rowSpan={5} className="w-1/3 p-4 border-l border-gray-300 border-solid align-top">
                    <div className="flex items-start justify-center">
                      {finalProfile?.profilePhoto ? (
                        <figure className="m-0 w-[200px] h-[200px]">
                          <img
                            src={convertS3UrlToProxyUrl(finalProfile.profilePhoto)}
                            alt={
                              finalProfile.alias || finalProfile.realName
                                ? t('threatReport.media.profilePhoto', {
                                  name: finalProfile.alias || finalProfile.realName,
                                })
                                : t('threatReport.media.profilePhotoUnknown')
                            }
                            width={200}
                            height={200}
                            className="w-[200px] h-[200px] object-cover rounded-lg"
                            loading="eager"
                          />
                        </figure>
                      ) : (
                        <div className="w-[200px] h-[200px] bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                          <UserIcon size={64} weight="regular" className="text-gray-400" />
                        </div>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Row 2: DOB and Class. */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.dateOfBirth')}</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.dob ? <time dateTime={finalProfile.dob}>{finalProfile.dob}</time> : ''}
                  </td>
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.classification')}</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.classification ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-md text-[color:var(--text-primary,#0f1d32)]" style={{ backgroundColor: classificationBoxColor }}>
                        {finalProfile.classification}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div>
                    ) : ''}
                  </td>
                </tr>

                {/* Row 3: Address and Group */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.address')}</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.address || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.group')}</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.group ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-md text-[color:var(--text-primary,#0f1d32)]" style={{ backgroundColor: groupBoxColor }}>
                        {finalProfile.group}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div>
                    ) : ''}
                  </td>
                </tr>

                {/* Row 4: City, State and Ideology */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.cityState')}</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.cityState || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.ideology')}</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.ideology || ''}</td>
                </tr>

                {/* Row 5: Email and Phone */}
                <tr>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.email')}</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.email && finalProfile.email !== 'N/A' ? (
                      <a href={`mailto:${finalProfile.email}`}>{finalProfile.email}</a>
                    ) : (
                      t('threatReport.field.notAvailable')
                    )}
                  </td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">{t('threatReport.field.phone')}</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.phone && finalProfile.phone !== 'N/A' ? (
                      <a href={`tel:${finalProfile.phone}`}>{finalProfile.phone}</a>
                    ) : (
                      t('threatReport.field.notAvailable')
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Contact Info & Social Media Accounts */}
          <section aria-labelledby="contacts-heading" className="mb-12">
            <h2 id="contacts-heading" className="mb-6 text-xl font-bold uppercase">{t('threatReport.body.contactsHeading')}</h2>

            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <caption className="sr-only">{t('threatReport.body.contactsCaption')}</caption>
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.crypto')}</th>
                  <th scope="col" className="w-[140px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.aliasProfileNames')}</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.platform')}</th>
                  <th scope="col" className="w-[120px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.idAccount')}</th>
                  <th scope="col" className="p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.url')}</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.dateCreated')}</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.publicPrivate')}</th>
                  <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.active')}</th>
                </tr>
              </thead>
              <tbody>
                {finalContactInfo && finalContactInfo.length > 0 ? (
                  finalContactInfo.map((contact, index) => (
                    <tr key={index} className="border-b border-gray-300 border-solid">
                      <td className="w-[80px] p-2 border-r border-gray-300 border-solid bg-white">{contact.crypto || t('threatReport.field.notAvailable')}</td>
                      <td className="w-[140px] p-2 border-r border-gray-300 border-solid bg-white">{contact.alias || ''}</td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">{contact.platform || ''}</td>
                      <td className="w-[120px] p-2 border-r border-gray-300 border-solid bg-white">{contact.idAccount || ''}</td>
                      <td className="p-2 border-r border-gray-300 border-solid bg-white max-w-0">
                        {contact.url ? <a href={contact.url} target="_blank" rel="noopener noreferrer" className="block truncate">{contact.url}</a> : ''}
                      </td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                        {contact.dateCreated ? <time dateTime={contact.dateCreated}>{contact.dateCreated}</time> : ''}
                      </td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                        {contact.publicPrivate ? (
                          <data value={contact.publicPrivate.toLowerCase()}>{contact.publicPrivate}</data>
                        ) : ''}
                      </td>
                      <td className="w-[80px] p-2 bg-white">
                        {contact.active ? (
                          <data value={contact.active.toLowerCase()}>{contact.active}</data>
                        ) : ''}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-[color:var(--text-secondary,#356189)] bg-white">{t('threatReport.body.noContacts')}</td>
                  </tr>
                )}
              </tbody>
            </table>

          {/* Threat Assessment Section */}
          <article id="threat-assessment" aria-labelledby="threat-assessment-heading" itemScope itemType="https://schema.org/Report" className="mb-12 mt-12">
            <h2 id="threat-assessment-heading" itemProp="name" className="mb-6 text-xl font-bold uppercase">{t('threatReport.body.threatAssessment')}</h2>

            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <tbody>
                {/* CLASSIFICATION */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.classification')}</th>
                  <td className="w-2/3 p-3 bg-white">
                    {finalThreatAssessment?.classification ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-md text-[color:var(--text-primary,#0f1d32)]" style={{ backgroundColor: classificationBoxColor }}>
                        {finalThreatAssessment.classification}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div>
                    ) : ''}
                  </td>
                </tr>

                {/* GROUP AFFILIATION */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.groupAffiliation')}</th>
                  <td className="w-2/3 p-3 bg-white">
                    {finalThreatAssessment?.groupAffiliation ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-md text-[color:var(--text-primary,#0f1d32)]" style={{ backgroundColor: groupBoxColor }}>
                        {finalThreatAssessment.groupAffiliation}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div>
                    ) : ''}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">{t('threatReport.field.ideology')}</th>
                  <td className="w-2/3 p-3 bg-white">{finalThreatAssessment?.ideology || ''}</td>
                </tr>
              </tbody>
            </table>
            </article>

            {/* Narrative Threat Assessment Report */}
            {finalNarrativeThreatAssessmentMd && (
              <article id="threat-report-assessment" aria-labelledby="narrative-assessment-heading" className="threat-report-assessment">
                <h2 id="narrative-assessment-heading" className="sr-only">{t('threatReport.body.narrative')}</h2>
                <ReactMarkdown
                  className="text-[color:var(--text-primary,#0f1d32)]"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={markdownRehypeRawSanitizePlugins}
                >
                  {finalNarrativeThreatAssessmentMd}
                </ReactMarkdown>
              </article>
            )}

            {/* Key Associates Subsection */}
            {finalKeyAssociates && finalKeyAssociates.length > 0 && (
              <section aria-labelledby="key-associates-heading" className="mt-8">
                <h2 id="key-associates-heading" className="mb-6 text-lg font-bold uppercase">{t('threatReport.body.keyAssociates')}</h2>

                <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
                  <caption className="sr-only">{t('threatReport.body.keyAssociatesCaption')}</caption>
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="w-[140px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.aliasProfileNames')}</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.platform')}</th>
                      <th scope="col" className="w-[120px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.idAccount')}</th>
                      <th scope="col" className="p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.url')}</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.dateCreated')}</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.publicPrivate')}</th>
                      <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-b border-gray-300 border-solid bg-gray-50">{t('threatReport.field.active')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalKeyAssociates.map((associate, index) => (
                      <tr key={index} className="border-b border-gray-300 border-solid">
                        <td className="w-[140px] p-2 border-r border-gray-300 border-solid bg-white">{associate.alias || ''}</td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">{associate.platform || ''}</td>
                        <td className="w-[120px] p-2 border-r border-gray-300 border-solid bg-white">{associate.idAccount || ''}</td>
                        <td className="p-2 border-r border-gray-300 border-solid bg-white max-w-0">
                          {associate.url ? <a href={associate.url} target="_blank" rel="noopener noreferrer" className="block truncate">{associate.url}</a> : ''}
                        </td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                          {associate.dateCreated ? <time dateTime={associate.dateCreated}>{associate.dateCreated}</time> : ''}
                        </td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                          {associate.publicPrivate ? (
                            <data value={associate.publicPrivate.toLowerCase()}>{associate.publicPrivate}</data>
                          ) : ''}
                        </td>
                        <td className="w-[80px] p-2 bg-white">
                          {associate.active ? (
                            <data value={associate.active.toLowerCase()}>{associate.active}</data>
                          ) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </section>

          {/* Actionable Threats Section */}
          {finalActionableThreats && finalActionableThreats.length > 0 && (
            <section id="actionable-threats" aria-labelledby="actionable-threats-heading" className="mb-12">
              <h2 id="actionable-threats-heading" className="mb-6 text-xl font-bold uppercase">{t('threatReport.body.actionableThreats')}</h2>
              <ol className="actionable-threats list-decimal list-inside space-y-2">
                {finalActionableThreats.map((threat, index) => (
                  <li key={index} className="text-[color:var(--text-primary,#0f1d32)]">
                    {threat.date && <strong>{threat.date}</strong>}
                    {threat.date && threat.evidenceSummary && ': '}
                    {threat.evidenceSummary && <span>{threat.evidenceSummary}</span>}
                    {threat.link && (
                      <span> (<a href={threat.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-block max-w-[600px] truncate">{threat.link}</a>)</span>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Social Media Posts Section */}
          {finalSocialMediaPosts && finalSocialMediaPosts.length > 0 && (
            <section id="social-posts" aria-labelledby="social-posts-heading" className="mb-12">
              <h2 id="social-posts-heading" className="mb-6 text-xl font-bold uppercase">{t('threatReport.body.socialPosts')}</h2>
              <div className="flex flex-col gap-6">
                {finalSocialMediaPosts.map((post, index) => (
                  <table key={index} className="post w-full border-collapse border border-gray-300 border-solid">
                    <tbody>
                      {/* Header Section */}
                      <tr>
                        <td colSpan={2} className="bg-gray-200 px-4 py-2 border-b border-gray-300 border-solid">
                          {post.date && <time dateTime={post.date} className="font-bold uppercase">{post.date}</time>}
                        </td>
                      </tr>
                      {/* Content Area - Two Column Layout */}
                      <tr className="bg-white">
                        {/* Left Column - Text Content */}
                        <td className="w-1/2 p-4 border-r border-gray-300 border-solid align-top">
                          <div className="text-md leading-relaxed whitespace-pre-wrap">
                            {post.notes || ''}
                          </div>
                        </td>
                        {/* Right Column - Media */}
                        <td className="w-1/2 p-4 align-top">
                          {post.media && post.media.length > 0 && (
                            <div className="w-full max-w-[300px] flex flex-col gap-4">
                              {post.media.map((mediaItem, mediaIndex) => (
                                <div key={mediaIndex} className="w-full">
                                  {mediaItem.url && mediaItem.url.trim() !== '' && (
                                    <>
                                      {mediaItem.type === 'image' ? (
                                        <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                                          <img
                                            src={convertS3UrlToProxyUrl(mediaItem.url)}
                                            alt={
                                              post.date
                                                ? t('threatReport.media.image', { index: mediaIndex + 1, date: post.date })
                                                : t('threatReport.media.imageUndated', { index: mediaIndex + 1 })
                                            }
                                            className="w-full h-full object-contain"
                                          />
                                        </div>
                                      ) : (
                                        <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                                          <video
                                            src={convertS3UrlToProxyUrl(mediaItem.url)}
                                            controls
                                            onError={(event) =>
                                              reportVideoFormatFailure({
                                                url: mediaItem.url,
                                                caseId,
                                                errorCode: event.currentTarget.error?.code,
                                              })
                                            }
                                            aria-label={
                                              post.date
                                                ? t('threatReport.media.video', { index: mediaIndex + 1, date: post.date })
                                                : t('threatReport.media.videoUndated', { index: mediaIndex + 1 })
                                            }
                                            className="w-full h-full object-contain"
                                          >
                                            {t('threatReport.media.videoUnsupported')}
                                          </video>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ))}
              </div>
            </section>
          )}


          </main>

          <footer className="mt-16 pt-4 border-t border-gray-300 text-sm">

            <section aria-label={t('threatReport.body.logos')} className="flex flex-row items-start justify-center mb-8">
              <figure className="flex justify-center m-0">
                <img src="/clarion-tr-logos@2x.png" alt={t('threatReport.body.logosAlt')} className="scale-50" />
              </figure>
            </section>

            <h1 className="flex flex-col items-center text-center mb-8 mt-4">{t('threatReport.body.endOfReport')}</h1>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                {t('threatReport.body.purpose')}
              </p>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                {t('threatReport.body.clarionNetwork')}
              </p>
              <p className="not-italic leading-relaxed max-w-4xl mx-auto">
                {textWithLinks(t('threatReport.body.visitOrEmail', { site: CLARION_SITE_URL, email: CLARION_EMAIL }), [
                  { text: CLARION_SITE_URL, href: CLARION_SITE_URL, external: true },
                  { text: CLARION_EMAIL, href: `mailto:${CLARION_EMAIL}` },
                ])}
              </p>
              <div className="flex flex-row justify-between items-center mt-4">
                <div>{t('threatReport.body.networkLine', { number: finalReportNumber })}</div>
              </div>
          </footer>

        </div>
      </div>
      )}
      </div>
    </Scrollbar>
    </div>
    </>
  );
};

export default ThreatReport;
